import type { Job } from "./types";

/**
 * Gets the job summary, preferring the pre-computed summary column
 * and falling back to computed summary from result JSON.
 *
 * This is the primary function to use for displaying job summaries
 * in the UI. It provides backward compatibility for jobs without
 * the summary column while preferring the pre-computed value when available.
 *
 * @param job - The job object containing optional summary and result data
 * @returns A human-readable summary string
 *
 * @example
 * // Job with pre-computed summary (preferred)
 * getJobSummary({ summary: "Updated 3 of 47 documents", ... })
 * // Returns: "Updated 3 of 47 documents"
 *
 * @example
 * // Job without summary (falls back to computed)
 * getJobSummary({ summary: null, job_type: 'content_update', result: { total: 47, updated: 3 }, ... })
 * // Returns: "3 of 47 documents updated"
 */
export function getJobSummary(job: Job): string {
  // Prefer the pre-computed summary column when available
  if (job.summary && job.summary.trim().length > 0) {
    return job.summary;
  }

  // Fall back to computed summary from result JSON
  return formatJobSummary(job);
}

/**
 * Formats a human-readable summary from a job's result data.
 * Handles all LocalDocs job types with type-specific formatting.
 *
 * Note: For UI display, prefer using getJobSummary() which
 * uses the pre-computed summary column when available.
 *
 * @param job - The job object containing result data
 * @returns A human-readable summary string
 *
 * @example
 * // Content update job
 * formatJobSummary({ job_type: 'content_update', result: { total: 47, updated: 3, failed: 0 } })
 * // Returns: "3 of 47 documents updated"
 *
 * @example
 * // Crawl documentation job
 * formatJobSummary({ job_type: 'crawl_documentation', result: { relevant_pages: 25, total_pages_discovered: 100 } })
 * // Returns: "25 relevant pages (100 discovered)"
 */
export function formatJobSummary(job: Job): string {
  if (!job.result) {
    return "No results available";
  }

  const result = job.result as Record<string, unknown>;

  switch (job.job_type) {
    case "content_update":
      return formatContentUpdateSummary(result);

    case "crawl_documentation":
      return formatCrawlDocumentationSummary(result);

    case "quality_check":
      return formatQualityCheckSummary(result);

    case "cleanup":
      return formatCleanupSummary(result);

    case "auto_fix":
      return formatAutoFixSummary(result);

    case "export":
      return formatExportSummary(result);

    default:
      return "Completed";
  }
}

/**
 * Formats content_update job result.
 * Expected fields: { total, updated, failed }
 */
function formatContentUpdateSummary(result: Record<string, unknown>): string {
  const total = (result.total as number) ?? 0;
  const updated = (result.updated as number) ?? 0;
  const failed = (result.failed as number) ?? 0;

  let summary = `${updated} of ${total} documents updated`;

  if (failed > 0) {
    summary += `, ${failed} failed`;
  }

  return summary;
}

/**
 * Formats crawl_documentation job result.
 * Expected fields: { relevant_pages, total_pages_discovered }
 */
function formatCrawlDocumentationSummary(result: Record<string, unknown>): string {
  const relevantPages = (result.relevant_pages as number) ?? 0;
  const totalDiscovered = (result.total_pages_discovered as number) ?? 0;

  return `${relevantPages} relevant pages (${totalDiscovered} discovered)`;
}

/**
 * Formats quality_check job result.
 * Expected fields: { high_priority, medium_priority, low_priority } or { total_issues }
 */
function formatQualityCheckSummary(result: Record<string, unknown>): string {
  const high = result.high_priority as number | undefined;
  const medium = result.medium_priority as number | undefined;
  const low = result.low_priority as number | undefined;

  // If we have priority breakdown
  if (high !== undefined || medium !== undefined || low !== undefined) {
    const totalIssues = (high ?? 0) + (medium ?? 0) + (low ?? 0);

    if (totalIssues === 0) {
      return "No issues found";
    }

    return `${high ?? 0} high, ${medium ?? 0} medium, ${low ?? 0} low priority issues`;
  }

  // Fall back to total_issues if available
  const totalIssues = result.total_issues as number | undefined;
  if (totalIssues !== undefined) {
    return `${totalIssues} issues found`;
  }

  return "Quality check complete";
}

/**
 * Formats cleanup job result.
 * Expected fields: { deleted }
 */
function formatCleanupSummary(result: Record<string, unknown>): string {
  const deleted = (result.deleted as number) ?? 0;
  return `${deleted} items cleaned up`;
}

/**
 * Formats auto_fix job result.
 * Expected fields: { fixed }
 */
function formatAutoFixSummary(result: Record<string, unknown>): string {
  const fixed = (result.fixed as number) ?? 0;
  return `${fixed} issues auto-fixed`;
}

/**
 * Formats export job result.
 * Expected fields: { documents_exported }
 */
function formatExportSummary(result: Record<string, unknown>): string {
  const documentsExported = result.documents_exported as number | undefined;

  if (documentsExported !== undefined) {
    return `${documentsExported} documents exported`;
  }

  return "Export completed";
}
